import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../layouts/AdminLayout';
import ImageUploadBox from '../../components/ImageUploadBox';
import ConfirmDialog from '../../components/ConfirmDialog';
import {
  getGalleryImages,
  createGalleryImage,
  deleteGalleryImage
} from '../../services/galleryService';
import {
  Card, CardContent, Typography, Button, IconButton, Grid, Box, Chip,
  makeStyles, CircularProgress, Snackbar, Tooltip, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select,
  FormControl, InputLabel
} from '@material-ui/core';
import AddPhotoAlternateIcon from '@material-ui/icons/AddPhotoAlternate';
import DeleteIcon from '@material-ui/icons/Delete';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import VisibilityIcon from '@material-ui/icons/Visibility';
import CollectionsIcon from '@material-ui/icons/Collections';

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400,
    background: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
  },
  loadingText: {
    marginTop: 16,
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  card: {
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
    position: 'relative',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.3s ease',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
      borderColor: 'rgba(16,185,129,0.3)',
      '& $overlayActions': {
        opacity: 1,
      },
    },
  },
  imageWrapper: {
    position: 'relative',
    width: '100%',
    paddingTop: '65%', // 16:10 aspect ratio
    backgroundColor: '#0f172a',
    overflow: 'hidden',
  },
  cardImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.4s ease',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  overlayActions: {
    position: 'absolute',
    top: 8,
    right: 8,
    display: 'flex',
    gap: 6,
    opacity: 0.85,
    transition: 'opacity 0.2s ease',
  },
  actionChip: {
    background: 'rgba(0,0,0,0.65)',
    color: '#fff',
    backdropFilter: 'blur(6px)',
    '&:hover': {
      background: 'rgba(0,0,0,0.85)',
    },
  },
  categoryBadge: {
    position: 'absolute',
    top: 8,
    left: 8,
    background: 'rgba(16,185,129,0.85)',
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.75rem',
    backdropFilter: 'blur(4px)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
  },
  cardContent: {
    padding: '16px !important',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: 4,
    fontSize: '1rem',
  },
  desc: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    lineHeight: 1.4,
    marginBottom: 10,
    flex: 1,
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: 10,
    marginTop: 'auto',
  },
  date: {
    color: theme.palette.text.secondary,
    fontSize: '0.78rem',
  },
  uploadBtn: {
    borderRadius: 12,
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#fff',
    fontWeight: 600,
    padding: '8px 22px',
    boxShadow: '0 4px 12px rgba(16,185,129,0.3)',
    '&:hover': {
      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    },
  },
  searchField: {
    minWidth: 260,
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
    },
  },
  countChip: {
    background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.15) 100%)',
    color: '#10b981',
    border: '1px solid rgba(16,185,129,0.3)',
    fontWeight: 700,
    fontSize: '0.85rem',
    height: 36,
    padding: '0 8px',
    borderRadius: 10,
  },
  filterChips: {
    display: 'flex',
    gap: 8,
    overflowX: 'auto',
    marginBottom: theme.spacing(3),
    paddingBottom: 4,
  },
}));

const CATEGORIES = [
  'All',
  'Masjid & Holy Sites',
  'Quran & Calligraphy',
  'Islamic Architecture',
  'Ramadan & Eid',
  'Islamic Quotes & Art',
  'Nature & Creation',
  'General',
];

export default function GalleryListPage() {
  const classes = useStyles();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [search, setSearch] = useState('');

  // Upload dialog state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newImage, setNewImage] = useState({
    title: '',
    category: 'Masjid & Holy Sites',
    description: '',
    imageUrl: '',
  });

  // Lightbox preview dialog state
  const [previewImage, setPreviewImage] = useState(null);

  // Confirm delete
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getGalleryImages({
        category: selectedCategory === 'All' ? 'all' : selectedCategory,
        search,
      });
      setImages(data);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to load gallery images' });
    }
    setLoading(false);
  }, [selectedCategory, search]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSaveNew = async (e) => {
    e.preventDefault();
    if (!newImage.imageUrl) {
      setSnackbar({ open: true, message: 'Please upload or provide an image.' });
      return;
    }
    if (!newImage.title.trim()) {
      setSnackbar({ open: true, message: 'Image title is required.' });
      return;
    }

    setSaving(true);
    try {
      await createGalleryImage({
        title: newImage.title.trim(),
        category: newImage.category,
        description: newImage.description.trim(),
        imageUrl: newImage.imageUrl,
      });

      setSnackbar({ open: true, message: 'Image added to Gallery! Visible on Noor Web.' });
      setUploadOpen(false);
      setNewImage({
        title: '',
        category: 'Masjid & Holy Sites',
        description: '',
        imageUrl: '',
      });
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to add image. Try again.' });
    }
    setSaving(false);
  };

  const handleDeleteClick = (img) => {
    setImageToDelete(img);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!imageToDelete) return;
    try {
      await deleteGalleryImage(imageToDelete.id, imageToDelete.imageUrl);
      setSnackbar({ open: true, message: 'Image deleted from gallery' });
      loadData();
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to delete image' });
    }
    setConfirmOpen(false);
    setImageToDelete(null);
  };

  const formatDate = (dateObj) => {
    if (!dateObj) return '';
    const d = dateObj instanceof Date ? dateObj : new Date(dateObj);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <AdminLayout title="Image Gallery">
      <div className={classes.toolbar}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            className={classes.searchField}
            placeholder="Search gallery images..."
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>

        <Box display="flex" style={{ gap: 16 }} alignItems="center">
          <Chip label={`Total Images: ${images.length}`} className={classes.countChip} />
          <Button
            variant="contained"
            className={classes.uploadBtn}
            startIcon={<AddPhotoAlternateIcon />}
            onClick={() => setUploadOpen(true)}
          >
            Upload Gallery Image
          </Button>
        </Box>
      </div>

      {/* Category filter chips */}
      <Box className={classes.filterChips}>
        {CATEGORIES.map((cat) => (
          <Chip
            key={cat}
            label={cat}
            clickable
            onClick={() => setSelectedCategory(cat)}
            style={{
              fontWeight: selectedCategory === cat ? 700 : 500,
              background: selectedCategory === cat ? '#10b981' : undefined,
              color: selectedCategory === cat ? '#fff' : undefined,
            }}
          />
        ))}
      </Box>

      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress size={44} style={{ color: '#10b981' }} />
          <Typography className={classes.loadingText}>Loading gallery images...</Typography>
        </div>
      ) : images.length === 0 ? (
        <div className={classes.loadingContainer}>
          <CollectionsIcon style={{ fontSize: 52, color: '#94a3b8', marginBottom: 12 }} />
          <Typography variant="h6" color="textSecondary" style={{ fontWeight: 600 }}>
            No images in gallery
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ maxWidth: 400, textAlign: 'center', marginTop: 4 }}>
            Click "Upload Gallery Image" to upload photos to Firebase Storage. They will automatically show up on Noor Web's Islamic Gallery.
          </Typography>
        </div>
      ) : (
        <Grid container spacing={3}>
          {images.map((img) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={img.id}>
              <Card className={classes.card}>
                <div className={classes.imageWrapper}>
                  <img
                    src={img.imageUrl}
                    alt={img.title}
                    className={classes.cardImage}
                    loading="lazy"
                  />
                  <Chip label={img.category || 'General'} size="small" className={classes.categoryBadge} />

                  <div className={classes.overlayActions}>
                    <IconButton
                      size="small"
                      className={classes.actionChip}
                      onClick={() => setPreviewImage(img)}
                      title="View full image"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      className={classes.actionChip}
                      style={{ color: '#ef4444' }}
                      onClick={() => handleDeleteClick(img)}
                      title="Delete image"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </div>
                </div>

                <CardContent className={classes.cardContent}>
                  <Typography className={classes.title} noWrap title={img.title}>
                    {img.title}
                  </Typography>
                  {img.description && (
                    <Typography className={classes.desc} noWrap title={img.description}>
                      {img.description}
                    </Typography>
                  )}

                  <div className={classes.meta}>
                    <Typography className={classes.date}>
                      {formatDate(img.createdAt)}
                    </Typography>
                    <Typography variant="caption" style={{ color: '#10b981', fontWeight: 600 }}>
                      Live on Web
                    </Typography>
                  </div>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Upload Dialog */}
      <Dialog
        open={uploadOpen}
        onClose={() => !saving && setUploadOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { borderRadius: 20, padding: 8 } }}
      >
        <DialogTitle disableTypography style={{ fontWeight: 700, fontSize: '1.2rem' }}>
          Upload New Gallery Image
        </DialogTitle>
        <DialogContent>
          <form id="gallery-form" onSubmit={handleSaveNew}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ImageUploadBox
                  value={newImage.imageUrl}
                  onChange={(url) => setNewImage({ ...newImage, imageUrl: url })}
                  folder="gallery"
                  label="Upload Gallery Photo"
                  helperText="Upload to Firebase Storage (PNG, JPG, WebP)"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Image Title *"
                  value={newImage.title}
                  onChange={(e) => setNewImage({ ...newImage, title: e.target.value })}
                  placeholder="e.g. Masjid Al-Haram at Sunset"
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={newImage.category}
                    onChange={(e) => setNewImage({ ...newImage, category: e.target.value })}
                    label="Category"
                  >
                    {CATEGORIES.filter((c) => c !== 'All').map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  variant="outlined"
                  label="Description / Caption (Optional)"
                  value={newImage.description}
                  onChange={(e) => setNewImage({ ...newImage, description: e.target.value })}
                  placeholder="Brief description or Quranic verse..."
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px', gap: 8 }}>
          <Button
            onClick={() => setUploadOpen(false)}
            disabled={saving}
            variant="outlined"
            style={{ borderRadius: 10 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="gallery-form"
            variant="contained"
            className={classes.uploadBtn}
            disabled={saving}
          >
            {saving ? <CircularProgress size={22} color="inherit" /> : 'Save & Publish to Web'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Full Preview Lightbox Dialog */}
      <Dialog
        open={Boolean(previewImage)}
        onClose={() => setPreviewImage(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ style: { borderRadius: 20, overflow: 'hidden', backgroundColor: '#0f172a' } }}
      >
        {previewImage && (
          <Box>
            <img
              src={previewImage.imageUrl}
              alt={previewImage.title}
              style={{ width: '100%', maxHeight: '75vh', objectFit: 'contain', display: 'block' }}
            />
            <Box p={2.5} display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h6" style={{ color: '#fff', fontWeight: 700 }}>
                  {previewImage.title}
                </Typography>
                <Typography variant="caption" style={{ color: '#94a3b8' }}>
                  Category: {previewImage.category} • Uploaded: {formatDate(previewImage.createdAt)}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={() => setPreviewImage(null)}
                style={{ color: '#fff', borderColor: '#475569', borderRadius: 10 }}
              >
                Close
              </Button>
            </Box>
          </Box>
        )}
      </Dialog>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={confirmOpen}
        title="Delete Gallery Image?"
        message="Are you sure you want to delete this image? It will be removed from Firebase and will no longer show on Noor Web."
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </AdminLayout>
  );
}
